import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  Send, 
  Sparkles, 
  Paperclip, 
  CheckCheck, 
  ShieldCheck, 
  Clock, 
  User,
  MessageSquare
} from 'lucide-react';
import { fetchMessages, sendMessage } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Button from './Button';
import './ChatModal.css';

export default function ChatModal({ recipient, isOpen, onClose }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const messagesEndRef = useRef(null);

  // Poll messages every 3 seconds while chat modal is open
  const { data, isLoading } = useQuery({
    queryKey: ['messages', recipient?.id],
    queryFn: () => fetchMessages(recipient?.id),
    enabled: !!(isOpen && recipient?.id && user),
    refetchInterval: 3000,
  });

  const messages = data?.messages || [];

  // Scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: (text) => sendMessage(recipient.id, text),
    onSuccess: (res) => {
      setContent('');
      queryClient.invalidateQueries(['messages', recipient.id]);
      queryClient.invalidateQueries(['conversations']);
    },
  });

  if (!isOpen || !recipient) return null;

  const handleSend = (e) => {
    e.preventDefault();
    if (!content.trim() || sendMutation.isPending) return;
    sendMutation.mutate(content.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  return (
    <div className="chat-modal-backdrop" onClick={onClose}>
      <div className="chat-modal-window glass-panel animate-scale-in" onClick={e => e.stopPropagation()}>
        {/* ── Chat Header ── */}
        <div className="chat-header">
          <div className="chat-recipient-info">
            <div className="chat-avatar-wrapper">
              <img 
                src={recipient.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${recipient.id}`} 
                alt={recipient.display_name} 
                className="chat-avatar"
              />
              <span className="online-indicator" />
            </div>
            <div>
              <div className="chat-recipient-name-row">
                <h4>{recipient.display_name}</h4>
                {recipient.ghana_card_verified === 1 && (
                  <span className="verified-badge" title="Ghana Card Verified Pro">
                    <ShieldCheck size={13} />
                  </span>
                )}
              </div>
              <span className="chat-recipient-subtitle">
                {recipient.specialization || 'Creative Pro'} • Active on Buildive
              </span>
            </div>
          </div>

          <button className="chat-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* ── Chat Body (Messages Stream) ── */}
        <div className="chat-messages-container">
          <div className="chat-safety-notice">
            <ShieldCheck size={14} className="text-green" />
            <span>End-to-End Direct Chat. Hire, collaborate, or commission custom graphic assets.</span>
          </div>

          {isLoading ? (
            <div className="chat-loading-state">
              <div className="spinner" />
              <p>Connecting to live DM channel...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty-state">
              <div className="chat-empty-icon">
                <MessageSquare size={28} />
              </div>
              <h4>Start a conversation with {recipient.display_name}</h4>
              <p>Ask about custom design requests, PSD template licensing, or feedback on their portfolio!</p>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                return (
                  <div key={msg.id} className={`message-bubble-row ${isMe ? 'outgoing' : 'incoming'}`}>
                    {!isMe && (
                      <img 
                        src={recipient.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${recipient.id}`} 
                        alt={recipient.display_name} 
                        className="msg-small-avatar"
                      />
                    )}
                    <div className="message-content-box">
                      <div className="message-text">{msg.content}</div>
                      <div className="message-meta-row">
                        <span className="message-time">{timeStr}</span>
                        {isMe && <CheckCheck size={13} className={msg.read_at ? 'text-green' : 'text-subtle'} />}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* ── Chat Input Footer ── */}
        <form onSubmit={handleSend} className="chat-input-footer">
          <textarea
            rows={1}
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${recipient.display_name}... (Press Enter to send)`}
            className="chat-textarea"
            autoFocus
          />

          <button 
            type="submit" 
            disabled={!content.trim() || sendMutation.isPending}
            className="chat-send-btn"
          >
            <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
